import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiPlay, FiCheckCircle } from 'react-icons/fi';
import Register from '../../assets/creation_de_compte.png';
import Tuto from '../../assets/prise_en_main.png';
import Clôture from '../../assets/cloture_de_votre_1ere_paie.png';

const YrosProcess = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      id: 1,
      title: t('yrosProcess.steps.step1_title'),
      description: t('yrosProcess.steps.step1_desc'),
      content: (
        <div className="relative group">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <img
              src={Register}
              alt={t('yrosProcess.steps.step1_alt')}
              className="w-full h-64 object-cover rounded-lg"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      )
    },
    {
      id: 2,
      title: t('yrosProcess.steps.step2_title'),
      description: t('yrosProcess.steps.step2_desc'),
      content: (
        <div className="relative group">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <img
              src={Tuto}
              alt={t('yrosProcess.steps.step2_alt')}
              className="w-full h-64 object-cover rounded-lg"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      )
    },
    {
      id: 3,
      title: t('yrosProcess.steps.step3_title'),
      description: t('yrosProcess.steps.step3_desc'),
      content: (
        <div className="relative group">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <img
              src={Clôture}
              alt={t('yrosProcess.steps.step3_alt')}
              className="w-full h-64 object-cover rounded-lg"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      )
    },
    {
      id: 4,
      title: t('yrosProcess.steps.step4_title'),
      description: t('yrosProcess.steps.step4_desc'),
      content: (
        <div className="flex flex-col items-center justify-center h-64 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
          <div className="bg-white rounded-full p-4 shadow-sm mb-4">
            <FiCheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <p className="text-lg font-medium text-gray-800 text-center">
            {t('yrosProcess.steps.step4_welcome')}
          </p>
          <div className="mt-4 w-16 h-1 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"></div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (activeStep < steps.length) setActiveStep(activeStep + 1);
  };
  
  const prevStep = () => {
    if (activeStep > 1) setActiveStep(activeStep - 1);
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-6" aria-label={t('yrosProcess.aria_label')}>
      {/* Header avec design amélioré */}
      <div className="text-center mb-16 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
          <FiPlay className="w-4 h-4" />
          {t('yrosProcess.badge')}
        </div>
        <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-gray-900 bg-clip-text text-transparent mb-6 leading-tight">
          {t('yrosProcess.heading')}
        </h1>
        <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
          {t('yrosProcess.subtitle')}
        </p>
        <div className="mt-8 w-24 h-1 bg-gradient-to-r from-blue-500 to-yellow-500 rounded-full mx-auto"></div>
      </div>

      {/* Navigation arrows avec design amélioré */}
      <nav className="flex justify-center items-center mb-12 gap-8" aria-label={t('yrosProcess.steps_nav_aria')}>
        <button
          onClick={prevStep}
          disabled={activeStep === 1}
          aria-label={t('yrosProcess.prev_step_aria')}
          className="group relative p-4 rounded-full bg-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 focus:ring-2 focus:ring-blue-400 hover:scale-105 border border-gray-100"
        >
          <FiChevronLeft className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors" aria-hidden="true" />
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
        </button>

        <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-md border border-gray-100">
          <span className="text-sm font-medium text-gray-500">{t('yrosProcess.step_label')}</span>
          <span className="text-lg font-bold text-blue-600">{activeStep}</span>
          <span className="text-sm font-medium text-gray-400">{t('yrosProcess.step_of')} {steps.length}</span>
        </div>

        <button
          onClick={nextStep}
          disabled={activeStep === steps.length}
          aria-label={t('yrosProcess.next_step_aria')}
          className="group relative p-4 rounded-full bg-white shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 focus:ring-2 focus:ring-blue-400 hover:scale-105 border border-gray-100"
        >
          <FiChevronRight className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors" aria-hidden="true" />
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
        </button>
      </nav>

      {/* Steps container avec design amélioré */}
      <div className="flex flex-col md:flex-row gap-8 justify-center items-stretch max-w-7xl mx-auto">
        {steps.map((step, idx) => {
          // Logique d'affichage des étapes
          let isVisible = false;
          if (window.innerWidth < 768) {
            isVisible = activeStep === step.id;
          } else {
            const firstIdx = Math.max(0, Math.min(steps.length - 3, activeStep - 2));
            const lastIdx = firstIdx + 2;
            isVisible = idx >= firstIdx && idx <= lastIdx;
          }
          
          if (!isVisible) return null;
          
          const isActive = activeStep === step.id;
          
          return (
            <article
              key={step.id}
              className={`relative bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-8 transition-all duration-500 min-w-[320px] max-w-[400px] flex-1 border-2 hover:shadow-2xl group
                ${isActive 
                  ? 'ring-4 ring-blue-500/30 shadow-2xl scale-105 border-blue-200 bg-gradient-to-br from-blue-50 to-yellow-50' 
                  : 'opacity-75 hover:opacity-100 border-yellow-200 hover:border-yellow-300 hover:scale-102'
                }
              `}
              aria-current={isActive ? "step" : undefined}
              tabIndex={isActive ? 0 : -1}
            >
              {/* Effet de brillance au survol */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
              
              {/* Numéro de l'étape */}
              <div className="flex items-center mb-6">
                <div className={`relative bg-gradient-to-r ${isActive ? 'from-blue-500 to-blue-600' : 'from-blue-400 to-blue-500'} text-white rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold shadow-lg`}>
                  {step.id}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent"></div>
                </div>
              </div>
              
              {/* Titre et description */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">
                  {step.title}
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {step.description}
                </p>
              </div>
              
              {/* Contenu */}
              <div className="transition-all duration-300">
                {step.content}
              </div>
              
              {/* Indicateur d'étape active */}
              {isActive && (
                <div className="absolute top-4 right-4 bg-blue-500 text-white text-xs px-3 py-1 rounded-full font-medium shadow-lg">
                  {t('yrosProcess.current_badge')}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/* Progress indicator avec design amélioré */}
      <nav className="flex justify-center mt-16 space-x-3" aria-label={t('yrosProcess.progress_nav_aria')}>
        <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-full shadow-lg border border-gray-100">
          {steps.map((step) => (
            <button
              key={step.id}
              onClick={() => setActiveStep(step.id)}
              aria-label={t('yrosProcess.goto_step_aria', { id: step.id, title: step.title })}
              aria-current={activeStep === step.id ? "step" : undefined}
              className={`relative w-4 h-4 rounded-full transition-all duration-300 focus:ring-2 focus:ring-blue-400 hover:scale-125 ${
                activeStep === step.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            >
              {activeStep === step.id && (
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 animate-pulse opacity-50"></div>
              )}
            </button>
          ))}
        </div>
      </nav>
    </section>
  );
};

export default YrosProcess;
